<template>
  <div class="admin-dashboard">
    <!-- Header -->
    <header class="dashboard-header">
      <div class="header-content">
        <div class="header-left">
          <h1>🏢 {{ t('adminDashboard.title') }}</h1>
          <p>{{ t('adminDashboard.subtitle') }}</p>
        </div>
        
        <div class="header-right">
          <div class="admin-info">
            <span class="admin-name">👤 {{ adminStore.user?.username }}</span>
            <button @click="handleLogout" class="logout-btn">
              🚪 {{ t('navigation.logout') }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="dashboard-main">
      <div class="dashboard-grid">
        
        <!-- Metrics Cards -->
        <div class="metrics-section">
          <h2>📊 {{ t('adminDashboard.systemOverview') }}</h2>
          
          <div class="metrics-grid">
            <div class="metric-card total-companies">
              <div class="metric-icon">🏢</div>
              <div class="metric-content">
                <div class="metric-value">{{ metrics.totalCompanies }}</div>
                <div class="metric-label">{{ t('adminDashboard.totalCompanies') }}</div>
              </div>
            </div>

            <div class="metric-card active-companies">
              <div class="metric-icon">✅</div>
              <div class="metric-content">
                <div class="metric-value">{{ metrics.activeCompanies }}</div>
                <div class="metric-label">{{ t('adminDashboard.activeCompanies') }}</div>
              </div>
            </div>

            <div class="metric-card total-revenue">
              <div class="metric-icon">💰</div>
              <div class="metric-content">
                <div class="metric-value">${{ formatCurrency(metrics.totalRevenue) }}</div>
                <div class="metric-label">{{ t('adminDashboard.totalRevenue') }}</div>
              </div>
            </div>

            <div class="metric-card pending-invoices">
              <div class="metric-icon">📄</div>
              <div class="metric-content">
                <div class="metric-value">{{ metrics.pendingInvoices }}</div>
                <div class="metric-label">{{ t('adminDashboard.pendingInvoices') }}</div>
              </div>
            </div>
          </div>
        </div>



        <!-- Quick Actions -->
        <div class="actions-section">
          <h2>⚡ {{ t('adminDashboard.quickActions') }}</h2>
          
          <div class="actions-grid">
          <!-- AI Scheduler - featured card -->
          <router-link to="/saas/scheduling" class="action-card featured">
            <div class="action-icon">🤖</div>
            <div class="action-content">
              <h3>AI Scheduler</h3>
              <p>Manage AI-powered scheduling for tenant companies</p>
            </div>
          </router-link>

          <router-link to="/saas/companies" class="action-card">
            <div class="action-icon">🏢</div>
            <div class="action-content">
              <h3>{{ t('adminDashboard.manageCompanies') }}</h3>
              <p>{{ t('adminDashboard.manageCompaniesDesc') }}</p>
            </div>
          </router-link>

          <router-link to="/saas/invoices" class="action-card">
            <div class="action-icon">📄</div>
            <div class="action-content">
              <h3>{{ t('adminDashboard.invoiceManagement') }}</h3>
              <p>{{ t('adminDashboard.invoiceManagementDesc') }}</p>
            </div>
          </router-link>

          <button @click="refreshData" class="action-card refresh-action" :disabled="loading">
              <div class="action-icon">🔄</div>
              <div class="action-content">
                <h3>{{ t('adminDashboard.refreshData') }}</h3>
                <p>{{ t('adminDashboard.refreshDataDesc') }}</p>
              </div>
            </button>

            <button @click="exportData" class="action-card export-action">
              <div class="action-icon">📊</div>
              <div class="action-content">
                <h3>{{ t('adminDashboard.exportReports') }}</h3>
                <p>{{ t('adminDashboard.exportReportsDesc') }}</p>
              </div>
            </button>
          </div>
        </div>

        <!-- Recent Companies -->
        <div class="recent-section">
          <h2>🕐 {{ t('adminDashboard.recentCompanies') }}</h2>
          
          <div class="recent-list" v-if="recentCompanies.length">
            <div 
              v-for="company in recentCompanies" 
              :key="company.id"
              class="recent-item"
            >
              <div class="company-info">
                <div class="company-name">{{ company.name }}</div>
                <div class="company-meta">
                  Schema: {{ company.schema_name }} • 
                  Created: {{ formatDate(company.created_at) }}
                </div>
              </div>
              
              <div class="company-status">
                <span :class="['status-badge', company.is_active ? 'active' : 'inactive']">
                  {{ company.is_active ? t('adminDashboard.active') : t('adminDashboard.inactive') }}
                </span>
              </div>
            </div>
          </div>

          <div v-else-if="!loading" class="empty-state">
            <p>🏢 {{ t('adminDashboard.noCompaniesFound') }}</p>
          </div>

          <div v-if="loading" class="loading-state">
            <p>🔄 {{ t('adminDashboard.loadingRecentCompanies') }}</p>
          </div>
        </div>

        <!-- System Status -->
        <div class="status-section">
          <h2>🖥️ {{ t('adminDashboard.systemStatus') }}</h2>
          
          <div class="status-grid">
            <div class="status-item">
              <div class="status-indicator good"></div>
              <div class="status-content">
                <div class="status-label">{{ t('adminDashboard.database') }}</div>
                <div class="status-value">{{ t('adminDashboard.operational') }}</div>
              </div>
            </div>

            <div class="status-item">
              <div class="status-indicator good"></div>
              <div class="status-content">
                <div class="status-label">{{ t('adminDashboard.apiServices') }}</div>
                <div class="status-value">{{ t('adminDashboard.healthy') }}</div>
              </div>
            </div>

            <div class="status-item">
              <div class="status-indicator good"></div>
              <div class="status-content">
                <div class="status-label">{{ t('adminDashboard.session') }}</div>
                <div class="status-value">{{ sessionTime }} {{ t('adminDashboard.remaining') }}</div>
              </div>
            </div>

            <div class="status-item">
              <div class="status-indicator good"></div>
              <div class="status-content">
                <div class="status-label">{{ t('adminDashboard.memory') }}</div>
                <div class="status-value">{{ t('adminDashboard.normal') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAdminStore } from '../../stores/admin.js'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

const router = useRouter()
const adminStore = useAdminStore()

const loading = ref(false)
const sessionTimer = ref(null)

// Computed values
const metrics = computed(() => ({
  totalCompanies: adminStore.dashboard?.total_companies || 0,
  activeCompanies: adminStore.dashboard?.active_companies || 0,
  totalUsers: adminStore.dashboard?.total_users || 0,
  totalRevenue: 0, // Will be calculated from invoices
  pendingInvoices: Array.isArray(adminStore.invoices) ? adminStore.invoices.filter(inv => inv.status === 'pending').length : 0
}))
const recentCompanies = computed(() => 
  Array.isArray(adminStore.companies) ? adminStore.companies.slice(0, 5).sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  ) : []
)

const sessionTime = ref('8h 0m')

// Methods
function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0)
}

function formatDate(dateStr) {
  if (!dateStr) return 'N/A'
  
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return 'Invalid Date'
  
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

async function refreshData() {
  if (loading.value) return
  
  loading.value = true
  try {
    await Promise.all([
      adminStore.loadDashboardData(),
      adminStore.loadCompanies()
    ])
  } catch (error) {
    console.error('Failed to refresh data:', error)
  } finally {
    loading.value = false
  }
}

function exportData() {
  // Future implementation for data export
  alert('📊 Export functionality coming soon!')
}

async function handleLogout() {
  await adminStore.logout()
  router.push('/saas/login')
}

function updateSessionTime() {
  const sessionStart = adminStore.sessionStart
  if (!sessionStart) return
  
  const now = new Date()
  const elapsed = now - new Date(sessionStart)
  const sessionDuration = 8 * 60 * 60 * 1000 // 8 hours in ms
  const remaining = sessionDuration - elapsed
  
  if (remaining <= 0) {
    sessionTime.value = 'Expired'
    handleLogout()
    return
  }
  
  const hours = Math.floor(remaining / (60 * 60 * 1000))
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000))
  sessionTime.value = `${hours}h ${minutes}m`
}

// Lifecycle
onMounted(async () => {
  // Check authentication
  if (!await adminStore.checkSession()) {
    router.push('/saas/login')
    return
  }
  
  // Load initial data
  await refreshData()
  
  // Start session timer
  sessionTimer.value = setInterval(updateSessionTime, 60000) // Update every minute
  updateSessionTime()
})

onUnmounted(() => {
  if (sessionTimer.value) {
    clearInterval(sessionTimer.value)
  }
})
</script>

<style scoped>
.admin-dashboard {
  min-height: 100vh;
  background: #f7fafc;
}

.dashboard-header {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left h1 {
  margin: 0;
  color: #2d3748;
  font-size: 1.75rem;
  font-weight: bold;
}

.header-left p {
  margin: 0.25rem 0 0 0;
  color: #718096;
  font-size: 0.9rem;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.admin-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.admin-name {
  color: #4a5568;
  font-weight: 500;
}

.logout-btn {
  padding: 0.5rem 1rem;
  background: #e53e3e;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.logout-btn:hover {
  background: #c53030;
  transform: translateY(-1px);
}

.dashboard-main {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
}

.dashboard-grid {
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;
}

.metrics-section h2,
.actions-section h2,
.recent-section h2,
.status-section h2 {
  margin: 0 0 1rem 0;
  color: #2d3748;
  font-size: 1.25rem;
  font-weight: 600;
}

.metrics-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.metric-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.metric-icon {
  font-size: 2.5rem;
}

.metric-value {
  font-size: 2rem;
  font-weight: bold;
  color: #2d3748;
  line-height: 1;
}

.metric-label {
  font-size: 0.9rem;
  color: #718096;
  margin-top: 0.25rem;
}

.total-companies .metric-icon { color: #3182ce; }
.active-companies .metric-icon { color: #38a169; }
.total-revenue .metric-icon { color: #d69e2e; }
.pending-invoices .metric-icon { color: #e53e3e; }

.actions-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.action-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  width: 100%;
}

.action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.action-card.featured {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: 2px solid #667eea;
}

.action-card.featured .action-icon {
  color: white;
}

.action-card.featured .action-content h3 {
  color: white;
}

.action-card.featured .action-content p {
  color: rgba(255, 255, 255, 0.9);
}

.action-card.featured:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(102, 126, 234, 0.4);
}



.action-icon {
  font-size: 2rem;
  color: #667eea;
}

.action-content h3 {
  margin: 0 0 0.25rem 0;
  font-size: 1.1rem;
  color: #2d3748;
}

.action-content p {
  margin: 0;
  font-size: 0.9rem;
  color: #718096;
}

.recent-list {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.recent-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.recent-item:last-child {
  border-bottom: none;
}

.company-name {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.company-meta {
  font-size: 0.85rem;
  color: #718096;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-badge.active {
  background: #c6f6d5;
  color: #22543d;
}

.status-badge.inactive {
  background: #fed7d7;
  color: #742a2a;
}

.status-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.status-item {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.status-indicator.good {
  background: #38a169;
}

.status-label {
  font-size: 0.9rem;
  color: #718096;
}

.status-value {
  font-weight: 600;
  color: #2d3748;
  margin-top: 0.25rem;
}

.empty-state,
.loading-state {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: #718096;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Responsive Design */
@media (min-width: 768px) {
  .dashboard-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .metrics-section {
    grid-column: 1 / -1;
  }
}

@media (min-width: 1024px) {
  .dashboard-grid {
    grid-template-columns: 2fr 1fr;
  }
  
  .metrics-section {
    grid-column: 1 / -1;
  }
  
  .actions-section {
    grid-column: 1 / 2;
  }
  
  .recent-section,
  .status-section {
    grid-column: 2 / 3;
  }
}

@media (max-width: 768px) {
  .dashboard-main {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .admin-info {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>